<template>
    <AppSidebar>
        <!-- Main Content -->
        <div class="space-y-10">
            <!-- Page Header -->
            <div class="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-gray-100 dark:border-gray-800">
                <div>
                <div>
                    <h1 class="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-2">
                        Product Management
                    </h1>
                    <p class="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        Manage your products, digital assets, and platform features.
                    </p>
                </div>
                </div>
                <div class="flex flex-wrap gap-3" v-if="authStore.user?.role === 'admin'">
                    <button @click="openCategoryModal" 
                        class="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm">
                        <SwatchIcon class="w-4 h-4 mr-2" />
                        Manage Categories
                    </button>
                    <button @click="openProductModal()" 
                        class="inline-flex items-center px-6 py-3 bg-primary-600 text-white text-xs font-bold rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                        <PlusIcon class="w-4 h-4 mr-2" />
                        Add Product
                    </button>
                </div>
            </div>

            <!-- Stats Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <!-- Total Assets -->
                <div class="relative group">
                    <div class="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-blue-500 rounded-[32px] blur opacity-0 group-hover:opacity-10 transition duration-500"></div>
                    <div class="relative bg-white dark:bg-gray-800/50 rounded-[32px] p-8 border border-gray-100 dark:border-gray-700/50 transition-all hover:-translate-y-1">
                        <div class="flex items-center justify-between">
                            <div class="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-2xl border border-primary-100/50 dark:border-primary-800/30">
                                <Square2StackIcon class="w-6 h-6 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div class="text-right">
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Total Inventory</p>
                                <p class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ products.length }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Active -->
                <div class="relative group">
                    <div class="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-[32px] blur opacity-0 group-hover:opacity-10 transition duration-500"></div>
                    <div class="relative bg-white dark:bg-gray-800/50 rounded-[32px] p-8 border border-gray-100 dark:border-gray-700/50 transition-all hover:-translate-y-1">
                        <div class="flex items-center justify-between">
                            <div class="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100/50 dark:border-emerald-800/30">
                                <CheckCircleIcon class="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div class="text-right">
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Active Assets</p>
                                <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">{{ activeCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Featured -->
                <div class="relative group">
                    <div class="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-[32px] blur opacity-0 group-hover:opacity-10 transition duration-500"></div>
                    <div class="relative bg-white dark:bg-gray-800/50 rounded-[32px] p-8 border border-gray-100 dark:border-gray-700/50 transition-all hover:-translate-y-1">
                        <div class="flex items-center justify-between">
                            <div class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100/50 dark:border-amber-800/30">
                                <StarIcon class="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div class="text-right">
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Promoted Products</p>
                                <p class="text-2xl font-bold text-amber-600 dark:text-amber-400 tracking-tight">{{ featuredCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Users -->
                <div class="relative group">
                    <div class="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-[32px] blur opacity-0 group-hover:opacity-10 transition duration-500"></div>
                    <div class="relative bg-white dark:bg-gray-800/50 rounded-[32px] p-8 border border-gray-100 dark:border-gray-700/50 transition-all hover:-translate-y-1">
                        <div class="flex items-center justify-between">
                            <div class="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-2xl border border-purple-100/50 dark:border-purple-800/30">
                                <UsersIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div class="text-right">
                                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1">Total Clients</p>
                                <p class="text-2xl font-bold text-purple-600 dark:text-purple-400 tracking-tight">{{ formatUsers(products.reduce((acc, p) => acc + (p.total_users || 0), 0)) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-[32px] p-6 shadow-sm border border-gray-100 dark:border-gray-700/50">
                <div class="flex flex-wrap items-center gap-6">
                    <div class="flex-1 min-w-[300px]">
                        <div class="relative group">
                            <MagnifyingGlassIcon class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                            <input v-model="searchQuery" type="text" placeholder="Search products, IDs, or categories..."
                                class="w-full pl-12 pr-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                        </div>
                    </div>
                    <div class="flex items-center gap-4">
                        <select v-model="filterStatus"
                            class="px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-xs font-bold uppercase tracking-widest">
                            <option value="">Status: All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="coming_soon">Coming Soon</option>
                        </select>
                        <select v-model="filterCategory"
                            class="px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-xs font-bold uppercase tracking-widest">
                            <option value="">Category: All</option>
                            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- Products Table -->
            <div class="bg-white dark:bg-gray-800/50 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
                <div v-if="loading" class="p-20 text-center">
                    <div class="relative w-16 h-16 mx-auto mb-6">
                        <div class="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
                        <div class="absolute inset-0 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Fetching Products...</p>
                </div>

                <div v-else-if="filteredProducts.length === 0" class="p-20 text-center">
                    <div class="w-16 h-16 bg-gray-50 dark:bg-gray-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-gray-100 dark:border-gray-700/50 text-gray-400 dark:text-gray-600">
                        <Square2StackIcon class="w-8 h-8" />
                    </div>
                    <p class="text-sm font-bold text-gray-900 dark:text-white tracking-tight mb-1">No Products Found</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 font-medium italic mb-8">Your product list is currently empty</p>
                    <button @click="openProductModal()" 
                        class="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-xl text-xs font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                        <PlusIcon class="w-4 h-4 mr-2" />
                        Add First Product
                    </button>
                </div>

                <div v-else class="overflow-x-auto custom-scrollbar">
                    <table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                        <thead class="bg-gray-50/50 dark:bg-gray-900/40">
                            <tr>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Product Name</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Category</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Platform</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Featured</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Status</th>
                                <th class="px-8 py-4 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Stats</th>
                                <th class="px-8 py-4 text-right text-xs font-semibold text-gray-400 dark:text-gray-500 tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50 dark:divide-gray-800/50">
                            <tr v-for="product in filteredProducts" :key="product.id"
                                class="group hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-all">
                                <td class="px-8 py-6">
                                    <div class="flex items-center gap-5">
                                        <div class="w-14 h-14 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 border border-gray-100/50 dark:border-gray-700">
                                            <img v-if="product.icon_image_url" :src="product.icon_image_url"
                                                :alt="product.title" class="w-full h-full object-cover" />
                                            <div v-else
                                                class="w-full h-full bg-gradient-to-br from-primary-400 to-blue-400 flex items-center justify-center text-white font-black text-xl">
                                                {{ product.title?.charAt(0) }}
                                            </div>
                                        </div>
                                        <div>
                                            <div class="flex items-center gap-2 mb-0.5">
                                                <p class="text-sm font-bold text-gray-900 dark:text-white tracking-tight">{{ product.title }}</p>
                                                <span v-if="product.featured"
                                                    class="px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] font-bold rounded-full border border-amber-100/50 dark:border-amber-800/50">Promoted</span>
                                            </div>
                                            <p class="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{{ product.short_description || 'No description available' }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-8 py-6">
                                    <span v-if="product.category"
                                        class="px-3 py-1 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-[9px] font-black uppercase tracking-widest rounded-full border border-primary-100/50 dark:border-primary-800/50">
                                        {{ product.category.name }}
                                    </span>
                                    <span v-else class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest italic">Uncategorized</span>
                                </td>
                                <td class="px-8 py-6">
                                    <span
                                        class="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border"
                                        :class="getPlatformClass(product.platform)">
                                        {{ formatPlatform(product.platform) }}
                                    </span>
                                </td>
                                <td class="px-8 py-6">
                                    <button @click="toggleTop(product)"
                                        class="flex items-center gap-2 px-2.5 py-1 rounded-lg border transition-all active:scale-95"
                                        :class="product.is_top ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400' : 'bg-gray-50/50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 text-gray-400'">
                                        <StarIcon class="w-3.5 h-3.5" :class="{ 'fill-current': product.is_top }" />
                                        <span class="text-[10px] font-bold">{{ product.is_top ? 'Top' : 'Regular' }}</span>
                                    </button>
                                </td>
                                <td class="px-8 py-6">
                                    <span
                                        class="px-3 py-1 rounded-full text-[10px] font-bold border transition-all"
                                        :class="getStatusClass(product.status)">
                                        {{ formatStatus(product.status) }}
                                    </span>
                                </td>
                                    <div class="flex flex-col gap-1">
                                        <div class="flex items-center gap-1.5">
                                            <StarIcon class="w-3 h-3 text-amber-500 fill-current" />
                                            <span class="text-xs font-bold text-gray-800 dark:text-gray-200 tracking-tight">{{ product.rating || '0' }}</span>
                                            <span class="text-[10px] text-gray-400 dark:text-gray-500">({{ product.rating_count || 0 }})</span>
                                        </div>
                                        <div class="flex items-center gap-1.5">
                                            <UsersIcon class="w-3 h-3 text-gray-400" />
                                            <span class="text-[10px] font-medium text-gray-500 dark:text-gray-400">{{ formatUsers(product.total_users) }} Users</span>
                                        </div>
                                    </div>
                                <td class="px-8 py-6 text-right">
                                    <div class="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all scale-95 group-hover:scale-100">
                                        <button @click="openProductModal(product)"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-primary-600 hover:text-white transition-all shadow-sm"
                                            title="Edit Product">
                                            <PencilSquareIcon class="w-4 h-4" />
                                        </button>
                                        <router-link :to="'/products/' + product.uid" target="_blank"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                            title="View Public Link">
                                            <EyeIcon class="w-4 h-4" />
                                        </router-link>
                                        <button @click="confirmDelete(product)"
                                            class="p-2.5 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                                            title="Delete Product">
                                            <TrashIcon class="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>


            <!-- Product Modal -->
            <div v-if="showProductModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity" @click="showProductModal = false"></div>
                
                <div class="relative bg-white dark:bg-gray-800 rounded-[40px] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-100 dark:border-gray-700/50 flex flex-col">
                    <!-- Modal Header -->
                    <div class="px-10 py-8 border-b border-gray-50 dark:border-gray-700/30 flex items-center justify-between bg-white/80 dark:bg-gray-800/80 backdrop-blur-md sticky top-0 z-20">
                        <div>
                            <h2 class="text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-1">
                                {{ editingProduct ? 'Edit Product' : 'Add New Product' }}
                            </h2>
                            <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {{ editingProduct ? 'Modify product details and platform settings' : 'Configure and create a new digital product' }}
                            </p>
                        </div>
                        <button @click="showProductModal = false"
                            class="p-3 text-gray-400 hover:text-gray-900 dark:hover:white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-2xl transition-all">
                            <XMarkIcon class="w-6 h-6" />
                        </button>
                    </div>

                    <div class="flex-1 overflow-y-auto custom-scrollbar">
                        <!-- Tab Navigation -->
                        <div class="flex border-b border-gray-50 dark:border-gray-700/30 px-10 bg-gray-50/50 dark:bg-gray-900/10 sticky top-0 z-10 overflow-x-auto no-scrollbar">
                            <button v-for="tab in modalTabs" :key="tab.id" @click="activeModalTab = tab.id"
                                type="button"
                                :class="['px-8 py-5 text-xs font-bold uppercase tracking-widest transition-all relative whitespace-nowrap', 
                                activeModalTab === tab.id ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200']">
                                {{ tab.label }}
                                <div v-if="activeModalTab === tab.id" class="absolute bottom-0 left-8 right-8 h-1 bg-primary-600 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.4)]"></div>
                            </button>
                        </div>

                            <div class="px-10 py-10">
                                <!-- Basic Info Tab -->
                                <div v-show="activeModalTab === 'basic'">
                                    <form @submit.prevent="saveProduct" class="space-y-10">
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div class="space-y-2">
                                                <label class="flex items-center text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                    <DocumentTextIcon class="w-3 h-3 mr-2" />
                                                    Product Title *
                                                </label>
                                                <input v-model="productForm.title" type="text" required
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Category
                                                </label>
                                                <select v-model="productForm.category_id"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium appearance-none">
                                                    <option value="">Select Category</option>
                                                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div class="space-y-2">
                                            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                Short Description
                                            </label>
                                            <textarea v-model="productForm.short_description" rows="2"
                                                class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium resize-none"></textarea>
                                        </div>

                                        <div class="space-y-2">
                                            <label class="flex items-center text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                <DocumentTextIcon class="w-3 h-3 mr-2" />
                                                Product Description
                                            </label>
                                            <textarea v-model="productForm.long_description" rows="4"
                                                class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium"></textarea>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Platform
                                                </label>
                                                <select v-model="productForm.platform"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium">
                                                    <option value="web">Web</option>
                                                    <option value="mobile">Mobile</option>
                                                    <option value="desktop">Desktop</option>
                                                    <option value="cross-platform">Cross-Platform</option>
                                                </select>
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Status
                                                </label>
                                                <select v-model="productForm.status"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium">
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                    <option value="coming_soon">Coming Soon</option>
                                                </select>
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Starting Price ($)
                                                </label>
                                                <input v-model="productForm.price" type="number" step="0.01" min="0"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Total Customers
                                                </label>
                                                <input v-model="productForm.total_users" type="number" min="0"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Public Rating (0-5)
                                                </label>
                                                <input v-model="productForm.rating" type="number" step="0.1" min="0" max="5"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Total Reviews
                                                </label>
                                                <input v-model="productForm.rating_count" type="number" min="0"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Release Date
                                                </label>
                                                <input v-model="productForm.release_date" type="date"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Last Update
                                                </label>
                                                <input v-model="productForm.last_updated" type="date"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Author / Developer
                                                </label>
                                                <input v-model="productForm.developer_name" type="text"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Version
                                                </label>
                                                <input v-model="productForm.version" type="text" placeholder="e.g., 1.0.0"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                        </div>

                                        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Download URL
                                                </label>
                                                <input v-model="productForm.download_url" type="url"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Website URL
                                                </label>
                                                <input v-model="productForm.website_url" type="url"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pl-1">
                                                    Source Repository
                                                </label>
                                                <input v-model="productForm.source_url" type="url"
                                                    class="w-full px-5 py-3.5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                            </div>
                                        </div>

                                        <div class="flex items-center gap-8 pt-4">
                                            <label class="flex items-center gap-3 cursor-pointer group">
                                                <input v-model="productForm.featured" type="checkbox"
                                                    class="w-5 h-5 text-primary-600 rounded-lg border-gray-100 dark:border-gray-700 focus:ring-primary-500 bg-gray-50 dark:bg-gray-900 transition-all" />
                                                <span class="text-xs font-bold text-gray-600 dark:text-gray-400">Promote to Featured</span>
                                            </label>
                                            <label class="flex items-center gap-3 cursor-pointer group">
                                                <input v-model="productForm.is_top" type="checkbox"
                                                    class="w-5 h-5 text-amber-500 rounded-lg border-gray-100 dark:border-gray-700 focus:ring-amber-500 bg-gray-50 dark:bg-gray-900 transition-all" />
                                                <span class="text-xs font-bold text-gray-600 dark:text-gray-400">Mark as Top Product</span>
                                            </label>
                                        </div>

                                        <!-- Image Uploads -->
                                        <div v-if="editingProduct" class="space-y-8 border-t border-gray-50 dark:border-gray-700/30 pt-10">
                                            <div class="flex items-center gap-3 mb-2">
                                                <div class="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                                                <h4 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">Visual Assets</h4>
                                            </div>
                                            <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                                                <div class="space-y-4">
                                                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                        Product Icon
                                                    </label>
                                                    <div class="flex items-center gap-6">
                                                        <div class="w-20 h-20 rounded-3xl overflow-hidden bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/50 flex-shrink-0 relative group">
                                                            <img v-if="editingProduct.icon_image_url"
                                                                :src="editingProduct.icon_image_url"
                                                                class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                            <div v-else class="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                                                                <SwatchIcon class="w-8 h-8" />
                                                            </div>
                                                        </div>
                                                        <div class="flex-1">
                                                            <input type="file" @change="handleIconUpload" accept="image/*"
                                                                class="block w-full text-[10px] text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-primary-50 file:text-primary-600 dark:file:bg-primary-900/20 dark:file:text-primary-400 hover:file:bg-primary-100 transition-all cursor-pointer" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="space-y-4">
                                                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                        Main Banner
                                                    </label>
                                                    <div class="flex items-center gap-6">
                                                        <div class="w-24 h-16 rounded-2xl overflow-hidden bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/50 flex-shrink-0 relative group">
                                                            <img v-if="editingProduct.banner_image_url"
                                                                :src="editingProduct.banner_image_url"
                                                                class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                            <div v-else class="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                                                                <TableCellsIcon class="w-10 h-10" />
                                                            </div>
                                                        </div>
                                                        <div class="flex-1">
                                                            <input type="file" @change="handleBannerUpload" accept="image/*"
                                                                class="block w-full text-[10px] text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-primary-50 file:text-primary-600 dark:file:bg-primary-900/20 dark:file:text-primary-400 hover:file:bg-primary-100 transition-all cursor-pointer" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                <!-- Screenshots Tab -->
                                <div v-show="activeModalTab === 'screenshots'" class="space-y-8">
                                    <!-- Message for new products -->
                                    <div v-if="!editingProduct" class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                        <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                            <TableCellsIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Service Registration Required</h4>
                                        <p class="text-sm text-gray-500 dark:text-gray-400 font-medium max-w-sm mx-auto">
                                            Please save the product details before adding screenshots.
                                        </p>
                                    </div>
                                    
                                    <!-- Existing screenshot content -->
                                    <div v-else class="space-y-10">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-3">
                                                <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                                                <h4 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">Product Screenshots</h4>
                                            </div>
                                            <label class="inline-flex items-center px-6 py-3 bg-primary-600 text-white text-xs font-bold rounded-xl cursor-pointer hover:bg-primary-700 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                                                <PlusIcon class="w-4 h-4 mr-2" />
                                                Add Screenshot
                                                <input type="file" @change="handleScreenshotUpload" accept="image/*" class="hidden" />
                                            </label>
                                        </div>

                                        <div v-if="editingProduct.screenshots && editingProduct.screenshots.length > 0"
                                            class="grid grid-cols-2 lg:grid-cols-3 gap-6">
                                            <div v-for="screenshot in editingProduct.screenshots" :key="screenshot.id"
                                                class="relative group rounded-[28px] overflow-hidden border border-gray-100 dark:border-gray-700/50 shadow-sm aspect-video bg-gray-50 dark:bg-gray-900/50">
                                                <img :src="screenshot.image_url" :alt="screenshot.caption"
                                                    class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                                <div class="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                                                    <div class="flex items-center justify-between">
                                                        <p class="text-[9px] font-black text-white uppercase tracking-widest truncate max-w-[150px]">
                                                            {{ screenshot.caption || 'Visual Asset' }}
                                                        </p>
                                                        <button @click="deleteScreenshot(screenshot.id)"
                                                            class="p-2.5 bg-rose-500 text-white rounded-xl hover:bg-rose-600 transition-all shadow-lg shadow-rose-500/20 active:scale-90">
                                                            <TrashIcon class="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                            <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                                <TableCellsIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h4 class="text-lg font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest">No visual nodes detected</h4>
                                            <p class="text-[9px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest mt-2">Initialize your first visual asset above</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Technical Features Tab -->
                                <div v-show="activeModalTab === 'features'" class="space-y-8">
                                    <!-- Message for new products -->
                                    <div v-if="!editingProduct" class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                        <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                            <CpuChipIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h4 class="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-3">Feature Registry Locked</h4>
                                        <p class="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-sm mx-auto">
                                            Save the basic information before managing product features.
                                        </p>
                                    </div>
                                    
                                    <!-- Existing features content -->
                                    <div v-else class="space-y-10">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-3">
                                                <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                                                <h4 class="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">Product Features</h4>
                                            </div>
                                            <button @click="showFeatureForm = !showFeatureForm"
                                                class="inline-flex items-center px-6 py-3 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-600 transition-all shadow-lg active:scale-95">
                                                <PlusIcon class="w-4 h-4 mr-2" />
                                                {{ showFeatureForm ? 'Dismiss Form' : 'Register Feature' }}
                                            </button>
                                        </div>

                                        <!-- Add Feature Form -->
                                        <div v-if="showFeatureForm" class="bg-gray-50 dark:bg-gray-900/30 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 space-y-6">
                                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div class="space-y-2">
                                                    <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Feature Title *</label>
                                                    <input v-model="newFeature.title" type="text"
                                                        class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                                                </div>
                                                <div class="space-y-2">
                                                    <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Visual Metric (Icon/Emoji)</label>
                                                    <input v-model="newFeature.icon" type="text" placeholder="e.g., ⚡"
                                                        class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                                                </div>
                                            </div>
                                            <div class="space-y-2">
                                                <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Protocol Description *</label>
                                                <textarea v-model="newFeature.description" rows="3"
                                                    class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium resize-none"></textarea>
                                            </div>
                                            <div class="flex justify-end gap-3 pt-2">
                                                <button @click="addTechnicalFeature" 
                                                    class="px-8 py-3.5 bg-primary-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                                                    Finalize Node
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="editingProduct.technical_features && editingProduct.technical_features.length > 0"
                                            class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div v-for="feature in editingProduct.technical_features" :key="feature.id"
                                                class="group relative bg-white dark:bg-gray-800/40 p-6 rounded-[28px] border border-gray-100 dark:border-gray-700/50 hover:border-primary-500/30 transition-all hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/20 overflow-hidden">
                                                <div class="flex items-start gap-5">
                                                    <div class="flex-shrink-0 w-12 h-12 rounded-2xl bg-gray-50 dark:bg-gray-900/50 flex items-center justify-center text-xl border border-gray-100 dark:border-gray-700/50 transition-colors group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 group-hover:border-primary-100/50 dark:group-hover:border-primary-800/50">
                                                        <span v-if="feature.icon">{{ feature.icon }}</span>
                                                        <CpuChipIcon v-else class="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
                                                    </div>
                                                    <div class="flex-1 min-w-0">
                                                        <h5 class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                            {{ feature.title }}
                                                        </h5>
                                                        <p class="text-[12px] text-gray-500 dark:text-gray-400 font-medium leading-relaxed line-clamp-2">
                                                            {{ feature.description }}
                                                        </p>
                                                    </div>
                                                    <button @click="deleteTechnicalFeature(feature.id)"
                                                        class="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all">
                                                        <TrashIcon class="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                            <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                                <CpuChipIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h4 class="text-lg font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest">No technology modules detected</h4>
                                            <p class="text-[9px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest mt-2">Initialize your first technical node above</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Testimonials Tab -->
                                <div v-show="activeModalTab === 'testimonials'" class="space-y-8">
                                    <!-- Message for new products -->
                                    <div v-if="!editingProduct" class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                        <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                            <StarIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h4 class="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-3">Feedback Stream Offline</h4>
                                        <p class="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-sm mx-auto">
                                            Initialize the module deployment before registering verified user testimonials.
                                        </p>
                                    </div>
                                    
                                    <!-- Existing testimonials content -->
                                    <div v-if="editingProduct" class="space-y-10">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-3">
                                                <div class="w-1.5 h-1.5 bg-yellow-400 rounded-full shadow-[0_0_8px_rgba(250,204,21,0.5)]"></div>
                                                <h4 class="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-[0.2em]">Verified Feedback Nodes</h4>
                                            </div>
                                            <button @click="showTestimonialForm = !showTestimonialForm"
                                                class="inline-flex items-center px-6 py-3 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-600 transition-all shadow-lg active:scale-95">
                                                <PlusIcon class="w-4 h-4 mr-2" />
                                                {{ showTestimonialForm ? 'Dismiss Form' : 'Register Feedback' }}
                                            </button>
                                        </div>

                                        <!-- Add Testimonial Form -->
                                        <div v-if="showTestimonialForm" class="bg-gray-50 dark:bg-gray-900/30 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 space-y-6">
                                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div class="space-y-2">
                                                    <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Identity Name *</label>
                                                    <input v-model="newTestimonial.name" type="text"
                                                        class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                                                </div>
                                                <div class="space-y-2">
                                                    <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Protocol Role/Title</label>
                                                    <input v-model="newTestimonial.role" type="text"
                                                        class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                                                </div>
                                            </div>
                                            <div class="space-y-2">
                                                <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Entity/Organization</label>
                                                <input v-model="newTestimonial.company" type="text"
                                                    class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium" />
                                            </div>
                                            <div class="space-y-2">
                                                <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Transmission Content *</label>
                                                <textarea v-model="newTestimonial.content" rows="3"
                                                    class="w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm font-medium resize-none"></textarea>
                                            </div>
                                            <div class="flex items-center justify-between pt-2">
                                                <div class="flex items-center gap-4">
                                                    <label class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Satisfaction Metric:</label>
                                                    <div class="flex gap-1.5">
                                                        <button v-for="star in 5" :key="star"
                                                            @click="newTestimonial.rating = star" type="button"
                                                            class="transform active:scale-125 transition-transform">
                                                            <StarIcon :class="star <= newTestimonial.rating ? 'w-5 h-5 text-yellow-400 fill-yellow-400' : 'w-5 h-5 text-gray-300 dark:text-gray-700'" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <button @click="addTestimonial" 
                                                    class="px-8 py-3.5 bg-primary-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/20 active:scale-95">
                                                    Archive Feedback
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="editingProduct.testimonials && editingProduct.testimonials.length > 0"
                                            class="grid grid-cols-1 gap-6">
                                            <div v-for="testimonial in editingProduct.testimonials" :key="testimonial.id"
                                                class="group relative bg-white dark:bg-gray-800/40 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 hover:border-primary-500/30 transition-all hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/20">
                                                <div class="flex flex-col md:flex-row gap-8">
                                                    <div class="flex-shrink-0 flex md:flex-col items-center gap-4">
                                                        <div class="w-16 h-16 rounded-[24px] bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center text-xl font-black text-primary-600 dark:text-primary-400 border border-gray-100 dark:border-gray-700/50 shadow-inner">
                                                            {{ testimonial.name?.charAt(0) }}
                                                        </div>
                                                        <div class="flex gap-0.5">
                                                            <StarIcon v-for="s in 5" :key="s"
                                                                :class="s <= testimonial.rating ? 'w-3.5 h-3.5 text-yellow-400 fill-yellow-400' : 'w-3.5 h-3.5 text-gray-200 dark:text-gray-800'" />
                                                        </div>
                                                    </div>
                                                    <div class="flex-1">
                                                        <div class="flex justify-between items-start mb-4">
                                                            <div>
                                                                <h5 class="text-base font-black text-gray-900 dark:text-white uppercase tracking-tight">{{ testimonial.name }}</h5>
                                                                <p class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">
                                                                    {{ testimonial.role }} <span v-if="testimonial.company" class="text-primary-500 dark:text-primary-400 mx-1">/</span> {{ testimonial.company }}
                                                                </p>
                                                            </div>
                                                            <button @click="deleteTestimonial(testimonial.id)"
                                                                class="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all">
                                                                <TrashIcon class="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                        <p class="text-[13px] text-gray-600 dark:text-gray-300 font-medium leading-relaxed italic">
                                                            "{{ testimonial.content }}"
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                            <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                                <StarIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h4 class="text-lg font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest">No verified feedback nodes</h4>
                                            <p class="text-[9px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest mt-2">Add your first user transmission above</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Installation Tab -->
                                <div v-show="activeModalTab === 'installation'" class="space-y-8">
                                    <!-- Message for new products -->
                                    <div v-if="!editingProduct" class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                        <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                            <Square2StackIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h4 class="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-3">Deployment Guide Encrypted</h4>
                                        <p class="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-sm mx-auto">
                                            Finalize the basic module parameters before initializing technical deployment protocols.
                                        </p>
                                    </div>
                                    
                                    <!-- Existing installation content -->
                                    <div v-if="editingProduct" class="space-y-12">
                                        <div class="space-y-6">
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                    <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                                                    <h4 class="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-[0.2em]">Deployment Logic</h4>
                                                </div>
                                                <button @click="saveInstallSteps" 
                                                    class="inline-flex items-center px-6 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-800 dark:hover:bg-gray-600 transition-all shadow-lg active:scale-95">
                                                    <ArrowPathIcon class="w-3.5 h-3.5 mr-2" />
                                                    Sync Steps
                                                </button>
                                            </div>
                                            <div class="space-y-3">
                                                <label class="flex items-center text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                    <DocumentTextIcon class="w-3 h-3 mr-2" />
                                                    Sequence Operations (One per line)
                                                </label>
                                                <textarea v-model="installStepsText" rows="8"
                                                    placeholder="01. Access terminal base&#10;02. Initialize extraction sequence&#10;03. Execute local binary distribution"
                                                    class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[24px] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 font-mono text-[13px] leading-relaxed transition-all custom-scrollbar"></textarea>
                                            </div>
                                        </div>

                                        <div class="space-y-6 pt-10 border-t border-gray-50 dark:border-gray-700/30">
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center gap-3">
                                                    <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                                                    <h4 class="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-[0.2em]">Module Prerequisites</h4>
                                                </div>
                                                <button @click="saveRequirements" 
                                                    class="inline-flex items-center px-6 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-800 dark:hover:bg-gray-600 transition-all shadow-lg active:scale-95">
                                                    <ArrowPathIcon class="w-3.5 h-3.5 mr-2" />
                                                    Sync Requirements
                                                </button>
                                            </div>
                                            <div class="space-y-3">
                                                <label class="flex items-center text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">
                                                    <CpuChipIcon class="w-3 h-3 mr-2" />
                                                    Hardware & Software Environment
                                                </label>
                                                <textarea v-model="requirementsText" rows="5"
                                                    placeholder="OS: Linux / MacOS / Windows&#10;Memory: 16GB Minimum&#10;Runtime: Node.js 18+"
                                                    class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[24px] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 font-mono text-[13px] leading-relaxed transition-all custom-scrollbar"></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Developers Tab -->
                                <div v-show="activeModalTab === 'developers'" class="space-y-8">
                                    <!-- Message for new products -->
                                    <div v-if="!editingProduct" class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                        <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                            <UsersIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                                        <h4 class="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-3">Unit Assignment Locked</h4>
                                        <p class="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-sm mx-auto">
                                            Deploy the base module configuration before assigning specialized developer units to this protocol.
                                        </p>
                                    </div>

                                    <div v-else class="space-y-10">
                                        <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                            <div class="flex items-center gap-3">
                                                <div class="w-1.5 h-1.5 bg-primary-500 rounded-full shadow-[0_0_8px_rgba(20,184,166,0.5)]"></div>
                                                <h4 class="text-[11px] font-black text-gray-900 dark:text-white uppercase tracking-[0.2em]">Active Unit Registry</h4>
                                            </div>
                                            <div class="flex items-center gap-3">
                                                <div class="relative flex-1 sm:min-w-[240px]">
                                                    <select v-model="selectedDeveloperId" 
                                                        class="w-full px-5 py-3 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-black uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all appearance-none cursor-pointer">
                                                        <option value="">Select Unit...</option>
                                                        <option v-for="dev in userStore.developers" :key="dev.id" :value="dev.id">
                                                            {{ dev.name }}
                                                        </option>
                                                    </select>
                                                    <div class="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                                        <ChevronDownIcon class="w-4 h-4" />
                                                    </div>
                                                </div>
                                                <button @click="assignDeveloper" :disabled="!selectedDeveloperId" 
                                                    class="inline-flex items-center px-6 py-3.5 bg-primary-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/20 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                                                    Assign Unit
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="editingProduct.developers && editingProduct.developers.length > 0" 
                                            class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div v-for="dev in editingProduct.developers" :key="dev.id" 
                                                class="group relative bg-white dark:bg-gray-800/40 p-6 rounded-[28px] border border-gray-100 dark:border-gray-700/50 hover:border-blue-500/30 transition-all hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/20">
                                                <div class="flex items-center gap-5">
                                                    <div class="relative">
                                                        <div class="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center border border-white dark:border-gray-700 shadow-sm transition-transform group-hover:scale-110">
                                                            <span class="text-blue-600 dark:text-blue-300 text-lg font-black">{{ dev.name?.charAt(0) }}</span>
                                                        </div>
                                                        <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"></div>
                                                    </div>
                                                    <div class="flex-1 min-w-0">
                                                        <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                            {{ dev.name }}
                                                        </p>
                                                        <p class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1 truncate">
                                                            {{ dev.email }}
                                                        </p>
                                                    </div>
                                                    <button @click="removeDeveloper(dev.id)" 
                                                        class="opacity-0 group-hover:opacity-100 p-2.5 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all">
                                                        <TrashIcon class="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="text-center py-20 bg-gray-50/50 dark:bg-gray-900/10 rounded-[32px] border border-dashed border-gray-100 dark:border-gray-700/50">
                                            <div class="inline-flex items-center justify-center p-5 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-6">
                                                <UserGroupIcon class="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <h4 class="text-lg font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest">No active units assigned</h4>
                                            <p class="text-[9px] font-black text-gray-400 dark:text-gray-600 uppercase tracking-widest mt-2">Initialize unit assignment from the registry above</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="px-10 py-8 bg-gray-50/50 dark:bg-gray-900/40 backdrop-blur-md flex justify-end gap-4 border-t border-gray-100 dark:border-gray-700/50">
                            <button @click="showProductModal = false"
                                class="px-8 py-3.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm active:scale-95">
                                Cancel Operation
                            </button>
                            <button @click="saveProduct" :disabled="saving" 
                                class="inline-flex items-center px-10 py-3.5 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                                <span v-if="!saving">{{ editingProduct ? 'Sync Module' : 'Initialize Protocol' }}</span>
                                <div v-else class="flex items-center gap-2">
                                    <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    <span>Processing...</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Category Management Modal -->
            <div v-if="showCategoryModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
                    <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity"
                        @click="showCategoryModal = false"></div>

                    <div
                        class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] transform transition-all sm:my-8 sm:max-w-2xl sm:w-full border border-white/20 dark:border-gray-800/50">
                        <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-4">
                                    <div class="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                                        <Square2StackIcon class="w-6 h-6 text-primary-500" />
                                    </div>
                                    <h3 class="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">Classification Matrix</h3>
                                </div>
                                <button @click="showCategoryModal = false"
                                    class="p-3 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-all">
                                    <XMarkIcon class="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div class="p-10">
                            <!-- Add Category Form -->
                            <form @submit.prevent="saveCategory" class="flex gap-4 mb-10">
                                <div class="flex-1 relative">
                                    <input v-model="categoryForm.name" type="text" placeholder="Entity Name" required
                                        class="w-full px-6 py-4 bg-gray-50/50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all font-medium" />
                                </div>
                                <button type="submit" :disabled="saving"
                                    class="px-8 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 disabled:opacity-50">
                                    {{ editingCategory ? 'Update Node' : 'Initialize Node' }}
                                </button>
                                <button v-if="editingCategory" @click="resetCategoryForm" type="button"
                                    class="px-6 py-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 rounded-2xl hover:bg-gray-50 transition-all">
                                    Dismiss
                                </button>
                            </form>

                            <!-- Category List -->
                            <div class="overflow-hidden bg-gray-50/30 dark:bg-gray-900/20 rounded-[32px] border border-gray-100 dark:border-gray-700/50">
                                <table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800/50">
                                    <thead>
                                        <tr class="bg-white dark:bg-gray-800/40">
                                            <th class="px-8 py-5 text-left text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Protocol ID</th>
                                            <th class="px-8 py-5 text-left text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Classification</th>
                                            <th class="px-8 py-5 text-right text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Operations</th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-100 dark:divide-gray-800/30">
                                        <tr v-for="cat in categories" :key="cat.id" class="hover:bg-white dark:hover:bg-gray-800/40 transition-colors">
                                            <td class="px-8 py-5 whitespace-nowrap text-xs font-mono text-gray-400 dark:text-gray-500">
                                                ID-{{ cat.id.toString().padStart(3, '0') }}
                                            </td>
                                            <td class="px-8 py-5 whitespace-nowrap text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">
                                                {{ cat.name }}
                                            </td>
                                            <td class="px-8 py-5 whitespace-nowrap text-right">
                                                <div class="flex justify-end gap-3">
                                                    <button @click="editCategory(cat)"
                                                        class="p-2.5 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all"
                                                        title="Edit">
                                                        <PencilSquareIcon class="w-4 h-4" />
                                                    </button>
                                                    <button @click="deleteCategory(cat.id)"
                                                        class="p-2.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all"
                                                        title="Delete">
                                                        <TrashIcon class="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
                    <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity"
                        @click="showDeleteModal = false"></div>

                    <div
                        class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] transform transition-all sm:my-8 sm:max-w-md sm:w-full p-10 border border-white/20 dark:border-gray-800/50">
                        <div class="text-center">
                            <div class="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 rounded-[32px] flex items-center justify-center mx-auto mb-8 shadow-lg shadow-rose-500/10">
                                <TrashIcon class="w-10 h-10 text-rose-500" />
                            </div>
                            <h3 class="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-4">Purge Module?</h3>
                            <p class="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose mb-10 pl-2">
                                Are you sure you want to purge <span class="text-rose-500">{{ deletingProduct?.title }}</span> from the core matrix? This action is irreversible.
                            </p>
                            <div class="flex flex-col gap-3">
                                <button @click="deleteProduct"
                                    class="w-full py-4 bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-rose-600 transition-all shadow-xl shadow-rose-500/20 active:scale-95">
                                    Execute Purge
                                </button>
                                <button @click="showDeleteModal = false"
                                    class="w-full py-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all active:scale-95">
                                    Abort Operation
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
  
    </AppSidebar>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useProductStore } from '@/stores/products'
import { useUserStore } from '@/stores/users'
import { useUIStore } from '@/stores/ui'
import apiService from '@/services/apiService'  
import { useAuthStore } from '@/stores/auth'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import { 
    PlusIcon, PencilSquareIcon, TrashIcon, EyeIcon, 
    TableCellsIcon, SwatchIcon, Square2StackIcon,
    CheckCircleIcon, StarIcon, UsersIcon,
    MagnifyingGlassIcon, XMarkIcon, ChevronRightIcon,
    CloudArrowUpIcon, DocumentTextIcon, UserGroupIcon,
    ChevronDownIcon, ArrowPathIcon, CpuChipIcon
} from '@heroicons/vue/24/outline';

const productStore = useProductStore()
const userStore = useUserStore()
const uiStore = useUIStore()
const authStore = useAuthStore()

const showProductModal = ref(false)
const showCategoryModal = ref(false)
const showDeleteModal = ref(false)
const editingProduct = ref(null)
const deletingProduct = ref(null)
const saving = ref(false)
const searchQuery = ref('')
const filterStatus = ref('')
const filterCategory = ref('')

// Tab and feature management state
const activeModalTab = ref('basic')
const showFeatureForm = ref(false)
const showTestimonialForm = ref(false)
const installStepsText = ref('')
const requirementsText = ref('')

// New feature/testimonial forms
const newFeature = ref({ title: '', description: '', icon: '' })
const newTestimonial = ref({ name: '', role: '', company: '', content: '', rating: 5 })
const selectedDeveloperId = ref('')

// Modal tabs configuration
const modalTabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'screenshots', label: 'Screenshots' },
    { id: 'features', label: 'Technical Features' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'installation', label: 'Installation' },
    { id: 'developers', label: 'Developers' }
]
const newCategoryName = ref('')

const defaultProductForm = {
    title: '',
    category_id: '',
    short_description: '',
    long_description: '',
    platform: 'web',
    status: 'active',
    price: 0,
    developer_name: '',
    version: '',
    download_url: '',
    website_url: '',
    source_url: '',
    featured: false,
    total_users: 0,
    rating: 0,
    rating_count: 0,
    release_date: '',
    last_updated: '',
    is_top: false
}

const productForm = ref({ ...defaultProductForm })

const loading = computed(() => productStore.loading)
const products = computed(() => productStore.products)
const categories = computed(() => productStore.categories)

const activeCount = computed(() => products.value.filter(p => p.status === 'active').length)
const featuredCount = computed(() => products.value.filter(p => p.featured).length)

const filteredProducts = computed(() => {
    let result = [...products.value]

    if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase()
        result = result.filter(p => p.title?.toLowerCase().includes(q) || p.short_description?.toLowerCase().includes(q))
    }

    if (filterStatus.value) {
        result = result.filter(p => p.status === filterStatus.value)
    }

    if (filterCategory.value) {
        result = result.filter(p => p.category_id === parseInt(filterCategory.value))
    }

    return result
})

onMounted(async () => {
    await productStore.fetchAdminProducts()
    await productStore.fetchAdminCategories()
    
    // Only initialize users if admin
    if (authStore.user?.role === 'admin') {
        await userStore.initializeUsers()
    }
})

const openProductModal = (product = null) => {
    if (product) {
        editingProduct.value = product
        productForm.value = {
            title: product.title || '',
            category_id: product.category_id || '',
            short_description: product.short_description || '',
            long_description: product.long_description || '',
            platform: product.platform || 'web',
            status: product.status || 'active',
            price: product.price || 0,
            developer_name: product.developer_name || '',
            version: product.version || '',
            download_url: product.download_url || '',
            website_url: product.website_url || '',
            source_url: product.source_url || '',
            featured: product.featured || false,
            total_users: product.total_users || 0,
            rating: product.rating || 0,
            rating_count: product.rating_count || 0,
            release_date: product.release_date ? product.release_date.split('T')[0] : '',
            last_updated: product.last_updated ? product.last_updated.split('T')[0] : '',
            is_top: product.is_top ? true : false
        }

        // Load installation steps and requirements if available
        if (product.install_instructions) {
            installStepsText.value = Array.isArray(product.install_instructions)
                ? product.install_instructions.join('\n')
                : product.install_instructions
        }
        if (product.requirements) {
            requirementsText.value = Array.isArray(product.requirements)
                ? product.requirements.join('\n')
                : product.requirements
        }
    } else {
        editingProduct.value = null
        productForm.value = { ...defaultProductForm }
        productForm.value.is_top = false
        installStepsText.value = ''
        requirementsText.value = ''
    }
    activeModalTab.value = 'basic'
    showProductModal.value = true
}

const saveProduct = async () => {
    if (!productForm.value.title?.trim()) return

    saving.value = true
    try {
        if (editingProduct.value) {
            await productStore.updateProduct(editingProduct.value.id, productForm.value)
        } else {
            await productStore.addProduct(productForm.value)
        }
        showProductModal.value = false
        await productStore.fetchAdminProducts()
    } catch (error) {
        console.error('Error saving product:', error)
    } finally {
        saving.value = false
    }
}

const toggleTop = async (product) => {
    try {
        await productStore.toggleTopProduct(product.id)
        uiStore.showNotification({
            type: 'success',
            title: 'Protocol Updated',
            message: `Product ${product.is_top ? 'removed from' : 'added to'} top list.`
        })
    } catch (error) {
        console.error('Failed to toggle top status:', error)
    }
}

const confirmDelete = (product) => {
    deletingProduct.value = product
    showDeleteModal.value = true
}

const deleteProduct = async () => {
    if (!deletingProduct.value) return

    try {
        await productStore.deleteProduct(deletingProduct.value.id)
        showDeleteModal.value = false
        deletingProduct.value = null
    } catch (error) {
        console.error('Error deleting product:', error)
    }
}

const handleIconUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !editingProduct.value) return

    try {
        await productStore.uploadIcon(editingProduct.value.id, file)
        await productStore.fetchAdminProducts()
    } catch (error) {
        console.error('Error uploading icon:', error)
    }
}

// Screenshot management
const handleScreenshotUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !editingProduct.value) return

    try {
        await productStore.uploadScreenshot(editingProduct.value.id, file)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated
    } catch (error) {
        console.error('Error uploading screenshot:', error)
    }
}

const deleteScreenshot = async (screenshotId) => {
    if (!confirm('Delete this screenshot?')) return

    try {
        await productStore.deleteScreenshot(screenshotId)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated
    } catch (error) {
        console.error('Error deleting screenshot:', error)
    }
}

// Technical features management
const addTechnicalFeature = async () => {
    if (!newFeature.value.title || !newFeature.value.description || !editingProduct.value) return

    try {
        await productStore.addFeature(editingProduct.value.id, newFeature.value)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated

        // Reset form
        newFeature.value = { title: '', description: '', icon: '' }
        showFeatureForm.value = false
    } catch (error) {
        console.error('Error adding feature:', error)
    }
}

const deleteTechnicalFeature = async (featureId) => {
    if (!confirm('Delete this feature?')) return

    try {
        await productStore.deleteFeature(featureId)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated
    } catch (error) {
        console.error('Error deleting feature:', error)
    }
}

// Testimonials management
const addTestimonial = async () => {
    if (!newTestimonial.value.name || !newTestimonial.value.content || !editingProduct.value) return

    try {
        await productStore.addTestimonial(editingProduct.value.id, newTestimonial.value)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated

        // Reset form
        newTestimonial.value = { name: '', role: '', company: '', content: '', rating: 5 }
        showTestimonialForm.value = false
    } catch (error) {
        console.error('Error adding testimonial:', error)
    }
}

const deleteTestimonial = async (testimonialId) => {
    if (!confirm('Delete this testimonial?')) return

    try {
        await productStore.deleteTestimonial(testimonialId)
        await productStore.fetchAdminProducts()
        // Refresh editing product
        const updated = products.value.find(p => p.id === editingProduct.value.id)
        if (updated) editingProduct.value = updated
    } catch (error) {
        console.error('Error deleting testimonial:', error)
    }
}

// Installation steps management
const saveInstallSteps = async () => {
    if (!editingProduct.value) return

    try {
        const steps = installStepsText.value.split('\n').filter(s => s.trim())
        await productStore.updateProduct(editingProduct.value.id, {
            ...productForm.value,
            install_instructions: steps
        })
        alert('Installation steps saved successfully!')
    } catch (error) {
        console.error('Error saving installation steps:', error)
        alert('Failed to save installation steps')
    }
}

const saveRequirements = async () => {
    if (!editingProduct.value) return

    try {
        const reqs = requirementsText.value.split('\n').filter(r => r.trim())
        await productStore.updateProduct(editingProduct.value.id, {
            ...productForm.value,
            requirements: reqs
        })
        alert('System requirements saved successfully!')
    } catch (error) {
        console.error('Error saving requirements:', error)
        alert('Failed to save requirements')
    }
}

const handleBannerUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !editingProduct.value) return

    try {
        await productStore.uploadBanner(editingProduct.value.id, file)
        await productStore.fetchAdminProducts()
    } catch (error) {
        console.error('Error uploading banner:', error)
    }
}

const addCategory = async () => {
    if (!newCategoryName.value.trim()) return

    try {
        await productStore.addCategory({ name: newCategoryName.value.trim() })
        newCategoryName.value = ''
        await productStore.fetchAdminCategories()
    } catch (error) {
        console.error('Error adding category:', error)
    }
}

const deleteCategory = async (id) => {
    if (!confirm('Are you sure you want to delete this category?')) return

    try {
        await productStore.deleteCategory(id)
    } catch (error) {
        console.error('Error deleting category:', error)
    }
}

const formatPlatform = (platform) => {
    const platforms = { 'mobile': 'Mobile', 'web': 'Web', 'desktop': 'Desktop', 'cross-platform': 'Cross-Platform' }
    return platforms[platform] || platform
}

const getPlatformClass = (platform) => {
    const classes = { 'mobile': 'bg-blue-100 text-blue-700', 'web': 'bg-green-100 text-green-700', 'desktop': 'bg-purple-100 text-purple-700', 'cross-platform': 'bg-orange-100 text-orange-700' }
    return classes[platform] || 'bg-gray-100 text-gray-700'
}

const formatStatus = (status) => {
    const statuses = { 'active': 'Active', 'inactive': 'Inactive', 'coming_soon': 'Coming Soon' }
    return statuses[status] || status
}

const getStatusClass = (status) => {
    const classes = { 'active': 'bg-green-100 text-green-700', 'inactive': 'bg-gray-100 text-gray-700', 'coming_soon': 'bg-yellow-100 text-yellow-700' }
    return classes[status] || 'bg-gray-100 text-gray-700'
}

const formatUsers = (count) => {
    if (!count) return '0'
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
    return count.toString()
}
const assignDeveloper = async () => {
    if (!selectedDeveloperId.value || !editingProduct.value) return

    try {
        uiStore.setLoading(true)
        const response = await apiService.post(`/products/${editingProduct.value.id}/assign-developer`, {
            user_id: selectedDeveloperId.value
        })

        if (response.success) {
            // Refresh editing product
            await productStore.fetchAdminProducts()
            const updated = products.value.find(p => p.id === editingProduct.value.id)
            if (updated) editingProduct.value = updated
            
            uiStore.showNotification({
                type: 'success',
                title: 'Developer Assigned',
                message: 'Developer has been assigned to this product.'
            })
            selectedDeveloperId.value = ''
        }
    } catch (error) {
        console.error('Failed to assign developer:', error)
        uiStore.showNotification({
            type: 'error',
            title: 'Assignment Failed',
            message: error.response?.data?.message || 'Failed to assign developer'
        })
    } finally {
        uiStore.setLoading(false)
    }
}

const removeDeveloper = async (developerId) => {
    if (!editingProduct.value) return

    if (confirm('Are you sure you want to remove this developer from the product?')) {
        try {
            uiStore.setLoading(true)
            const response = await apiService.post(`/products/${editingProduct.value.id}/remove-developer`, {
                user_id: developerId
            })

            if (response.success) {
                // Refresh editing product
                await productStore.fetchAdminProducts()
                const updated = products.value.find(p => p.id === editingProduct.value.id)
                if (updated) editingProduct.value = updated
                
                uiStore.showNotification({
                    type: 'success',
                    title: 'Developer Removed',
                    message: 'Developer has been removed from this product.'
                })
            }
        } catch (error) {
            console.error('Failed to remove developer:', error)
        } finally {
            uiStore.setLoading(false)
        }
    }
}
</script>
